import { post } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';

/**
 * Signing in with Google, for someone who may or may not have an account.
 *
 * Google already tells us everything the trimmed signup form asks for — a
 * name and a verified address — so there is nothing left to ask. A first
 * time user is created and signed in in one go and lands on Home, the same
 * place an email signup ends up. What they still need (hub, course, phone)
 * is collected when they enrol, not before.
 *
 * Previously this dropped them on the register form with the fields
 * pre-filled, then told them to "sign in with your email and password" —
 * a password a Google user does not have.
 */
export const signInWithGoogle = (
  credential: string,
  onDone: (session: any) => void,
  onError: (message: string) => void,
) => {
  const message = (err: any, fallback: string) =>
    err?.response?.body?.message || fallback;

  const authenticate = (isRetry: boolean) =>
    post(
      remoteRoutes.googleLogin,
      { credential, hubName: 'elevate' },
      (resp: any) => {
        if (!resp?.newUser) {
          onDone(resp);
          return;
        }
        if (isRetry) {
          // We just created the account, so the second call should have
          // found it. Rather than loop, say so plainly.
          onError('Account created, but signing in failed. Please log in.');
          return;
        }
        // First time through: create the account from the verified token.
        //
        // These fields are echoed back only to satisfy validation, which
        // runs before the controller and still requires a name and an
        // address. The server re-derives both from the signed token and
        // overwrites whatever is sent, so nothing here can be spoofed.
        // Some Google accounts carry no given/family name, so fall back to
        // the address rather than fail an empty-string check.
        const fallback = String(resp.email || '').split('@')[0] || 'Student';
        post(
          remoteRoutes.register,
          {
            googleCredential: credential,
            hubName: 'elevate',
            email: resp.email,
            firstName: resp.firstName || fallback,
            lastName: resp.lastName || fallback,
          },
          () => authenticate(true),
          (err: any) => onError(message(err, 'Could not create your account')),
        );
      },
      (err: any) => onError(message(err, 'Google sign-in failed')),
    );

  authenticate(false);
};
